//#region INTERFACE IViewChangesRow

import { StockItemUpdateFieldValue } from '../../../shared-utilities/models-old/datastructures';

/**
 * ### IViewChangesRow
 * Represents a single row of viewable change data, typically used in audit logs,
 * change comparison UIs, or revision history views.
 *
 * @interface IViewChangesRow
 * @extends Object
 * @defaults Not applicable
 * @description This interface encapsulates the properties required to represent a changed entity,
 *              including both the new (`n`) and original (`o`) values, identification fields,
 *              visibility control, and display metadata. It supports dynamic key-value structures,
 *              useful for generic diffing mechanisms or change history viewers.
 *
 * @example
 * const changeRow: IViewChangesRow = {
 *   code: 'ITEM-001',
 *   details: { name: 'Widget A', price: 10.99 },
 *   isNew: false,
 *   isParent: true,
 *   n: { name: 'Widget A', price: 12.99 },
 *   o: { name: 'Widget A', price: 10.99 },
 *   sortOrder: 1,
 *   visible: true
 * };
 */
export interface IViewChangesRow extends Object {
  /**
   * A unique identifier or code for the item represented in the change row.
   *
   * @type {string}
   * @example 'ITEM-001'
   */
  code: string;

  /**
   * A record of additional details associated with this row.
   * @description Used for display or tooltips; may contain supplementary metadata.
   *
   * @type {{ [key: string]: string | number | boolean | Date }}
   * @example { name: 'Widget A', updatedBy: 'admin' }
   */
  details: Record<string, string | number | boolean | Date>;

  /**
   * Indicates if the item is newly created (no previous state).
   *
   * @type {boolean}
   * @example true
   */
  isNew: boolean;

  /**
   * Indicates whether this row represents a parent entity (e.g., a grouped header row).
   *
   * @type {boolean}
   * @example false
   */
  isParent: boolean;

  /**
   * A record of the **new** values after the change.
   *
   * @type {{ [key: string]: string | number | boolean | Date | string[] }}
   * @example { price: 12.99 }
   */
  n: Record<string, StockItemUpdateFieldValue>;

  /**
   * A record of the **original** values before the change.
   *
   * @type {{ [key: string]: string | number | boolean | Date | string[] }}
   * @example { price: 10.99 }
   */
  o: Record<string, StockItemUpdateFieldValue>;

  /**
   * Defines the row's position in a sorted list, often used in UI tables.
   *
   * @type {number}
   * @example 1
   */
  sortOrder: number;

  /**
   * Controls whether this row is visible in the rendered output.
   *
   * @type {boolean}
   * @example true
   */
  visible: boolean;
}

//#endregion
